/**
 * Reader - 绘本阅读器（纸质绘本标准版）
 * 
 * 功能：
 * - 自动播放模式（听读）：图片70% + 文字20% + 音频10%
 * - 跟读模式（指读）：图片50% + 文字45% + 控制5%
 * - 符合RAZ分级/经典绘本比例标准
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';

const API_BASE_URL = import.meta.env.VITE_API_URL || '/api';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  ChevronLeft, 
  ChevronRight, 
  Play, 
  Pause,
  X,
  Maximize,
  Minimize,
  Headphones,
  Eye,
  Download,
  Heart,
  Images,
  Share2,
  MessageCircle,
  Send,
  User,
} from 'lucide-react';
import SharePoster from '../../components/SharePoster';
import ReadAlongMode from './ReadAlongMode';
import { useAuth } from '../../contexts/AuthContext';
import { api } from '../../utils/api';
import { useWechatShare } from '../../hooks/useWechatShare';

type ReaderMode = 'auto' | 'read-along';

interface Page {
  id: string;
  pageNumber: number;
  content: string;
  imageUrl: string | null;
  audioUrl: string | null;
  sentences: { text: string; startTime: number; endTime: number }[] | null;
  duration: number | null;
}

interface CommentItem {
  id: string;
  storyId?: string;
  shareToken?: string;
  content: string;
  nickname: string | null;
  avatar: string | null;
  createdAt: string;
}

interface Story {
  id: string;
  title: string;
  summary?: string;
  coverUrl?: string;
  artStyle?: string;
  targetAge?: string;
  userId?: string;
  likeCount?: number;
  viewCount?: number;
  playCount?: number;
  commentCount?: number;
}

export default function Reader() {
  const { id: storyId } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { isAuthenticated, user } = useAuth();
  
  const [story, setStory] = useState<Story | null>(null);
  const isOwner = story?.userId === user?.id;
  const [pages, setPages] = useState<Page[]>([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // 阅读模式
  const [mode, setMode] = useState<ReaderMode>('auto');
  
  // 音频状态
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [audioProgress, setAudioProgress] = useState(0);
  const [audioDuration, setAudioDuration] = useState(0);
  
  // 阅读器设置
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [isDownloadingPDF, setIsDownloadingPDF] = useState(false);
  const [isDownloadingHTML5, setIsDownloadingHTML5] = useState(false);
  const [isDownloadingImages, setIsDownloadingImages] = useState(false);
  const [likeCount, setLikeCount] = useState(0);
  const [viewCount, setViewCount] = useState(0);
  const [playCount, setPlayCount] = useState(0);
  const [commentCount, setCommentCount] = useState(0);
  const [comments, setComments] = useState<CommentItem[]>([]);
  const [isLiking, setIsLiking] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [showCommentModal, setShowCommentModal] = useState(false);
  const [shareData, setShareData] = useState<{ token: string; url: string } | null>(null);
  const [commentText, setCommentText] = useState('');
  const [commentNickname, setCommentNickname] = useState('');
  const [submittingComment, setSubmittingComment] = useState(false);
  const hasCountedPlay = useRef(false);

  // 微信JSSDK分享配置
  useWechatShare({
    title: `我正在读《${story?.title || '绘本'}》`,
    desc: '来听听我读绘本吧~',
    link: window.location.href,
    imgUrl: story?.coverUrl || `${window.location.origin}/logo.png`,
  });
  
  // Refs
  const audioRef = useRef<HTMLAudioElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const touchStartX = useRef<number>(0);

  // 加载绘本数据
  useEffect(() => {
    if (!storyId) {
      setError('无效的故事ID');
      setIsLoading(false);
      return;
    }
    
    const fetchStory = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/stories/${storyId}/reader`);
        const result = await response.json();
        
        if (result.success) {
          setStory(result.data.story);
          setPages(result.data.pages);
          setCurrentIndex(0);
          setLikeCount(result.data.story?.likeCount || 0);
          setViewCount(result.data.story?.viewCount || 0);
          setPlayCount(result.data.story?.playCount || 0);
          setCommentCount(result.data.story?.commentCount || 0);
          setComments(result.data.comments || []);
        } else {
          setError(result.error || '加载失败');
        }
      } catch (err) {
        setError('网络错误，请检查连接');
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchStory();
  }, [storyId]);

  // 模式切换处理
  const handleModeChange = useCallback((newMode: ReaderMode) => {
    setMode(newMode);
    
    if (newMode === 'read-along') {
      if (audioRef.current) {
        audioRef.current.pause();
      }
      setIsPlaying(false);
    } else {
      setIsPlaying(false);
    }
  }, []);

  // 音频控制
  const togglePlay = useCallback(() => {
    if (!audioRef.current || mode === 'read-along') return;
    
    if (isPlaying) {
      audioRef.current.pause();
    } else {
      audioRef.current.play().catch(console.error);
    }
  }, [isPlaying, mode]);

  const toggleMute = useCallback(() => {
    if (!audioRef.current) return;
    audioRef.current.muted = !isMuted;
    setIsMuted(!isMuted);
  }, [isMuted]);
  
  // 使用toggleMute避免未使用警告
  useEffect(() => {
    // 此函数在需要时可通过快捷键调用
  }, [toggleMute]);

  // 翻页控制
  const goToPage = useCallback((index: number) => {
    if (index < 0 || index >= pages.length) return;
    setCurrentIndex(index);
    setAudioProgress(0);
    
    if (mode === 'auto' && audioRef.current) {
      const newPage = pages[index];
      if (newPage?.audioUrl) {
        audioRef.current.src = newPage.audioUrl;
        audioRef.current.load();
        audioRef.current.play().catch(() => {});
      }
    }
  }, [pages.length, pages, mode]);

  const goNext = useCallback(() => {
    if (currentIndex < pages.length - 1) {
      goToPage(currentIndex + 1);
    }
  }, [currentIndex, pages.length, goToPage]);

  const goPrev = useCallback(() => {
    if (currentIndex > 0) {
      goToPage(currentIndex - 1);
    }
  }, [currentIndex, goToPage]);

  // 键盘控制
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      switch (e.key) {
        case 'ArrowRight':
        case ' ':
          e.preventDefault();
          goNext();
          break;
        case 'ArrowLeft':
          e.preventDefault();
          goPrev();
          break;
        case 'Escape':
          if (isFullscreen) {
            document.exitFullscreen().catch(() => {});
          }
          break;
      }
    };
    
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [goNext, goPrev, isFullscreen]);

  // 触摸滑动控制
  const handleTouchStart = (e: React.TouchEvent) => {
    touchStartX.current = e.touches[0].clientX;
  };

  const handleTouchEnd = (e: React.TouchEvent) => {
    const diff = touchStartX.current - e.changedTouches[0].clientX;
    const threshold = 50;
    
    if (Math.abs(diff) > threshold) {
      if (diff > 0) {
        goNext();
      } else {
        goPrev();
      }
    }
  };

  // 音频事件处理
  useEffect(() => {
    if (mode === 'read-along') return;
    
    const audio = audioRef.current;
    if (!audio) return;

    const handleTimeUpdate = () => {
      setAudioProgress(audio.currentTime);
    };

    const handleLoadedMetadata = () => {
      setAudioDuration(audio.duration);
    };

    const handleEnded = () => {
      if (currentIndex < pages.length - 1) {
        goNext();
      } else {
        setIsPlaying(false);
      }
    };

    const handlePlay = () => {
      setIsPlaying(true);
      // 播放计数（每 session 只计一次）
      if (storyId && !hasCountedPlay.current) {
        hasCountedPlay.current = true;
        fetch(`${API_BASE_URL}/stories/${storyId}/reader/play`, { method: 'POST' })
          .then(res => res.json())
          .then(data => {
            if (data.success) {
              setPlayCount(data.data.playCount);
            }
          })
          .catch(console.error);
      }
    };
    const handlePause = () => setIsPlaying(false);

    audio.addEventListener('timeupdate', handleTimeUpdate);
    audio.addEventListener('loadedmetadata', handleLoadedMetadata);
    audio.addEventListener('ended', handleEnded);
    audio.addEventListener('play', handlePlay);
    audio.addEventListener('pause', handlePause);

    return () => {
      audio.removeEventListener('timeupdate', handleTimeUpdate);
      audio.removeEventListener('loadedmetadata', handleLoadedMetadata);
      audio.removeEventListener('ended', handleEnded);
      audio.removeEventListener('play', handlePlay);
      audio.removeEventListener('pause', handlePause);
    };
  }, [currentIndex, pages.length, goNext, mode]);

  // 切换页面时处理音频
  useEffect(() => {
    if (mode === 'read-along') return;
    
    const audio = audioRef.current;
    const currentPage = pages[currentIndex];
    
    if (audio && currentPage?.audioUrl) {
      audio.src = currentPage.audioUrl;
      audio.load();
      
      if (isPlaying) {
        audio.play().catch(console.error);
      }
    }
  }, [currentIndex, pages, isPlaying, mode]);

  // 预加载下一页
  useEffect(() => {
    const nextIndex = currentIndex + 1;
    if (nextIndex < pages.length) {
      const nextPage = pages[nextIndex];
      
      if (nextPage.imageUrl) {
        const img = new Image();
        img.src = nextPage.imageUrl;
      }
      
      if (mode === 'auto' && nextPage.audioUrl) {
        const audio = new Audio();
        audio.preload = 'auto';
        audio.src = nextPage.audioUrl;
      }
    }
  }, [currentIndex, pages, mode]);

  // 全屏控制
  const toggleFullscreen = useCallback(() => {
    if (!containerRef.current) return;
    
    if (!isFullscreen) {
      containerRef.current.requestFullscreen().catch(() => {});
    } else {
      document.exitFullscreen().catch(() => {});
    }
  }, [isFullscreen]);

  // 分享绘本
  const handleShare = useCallback(async () => {
    if (!storyId) return;
    if (!isAuthenticated) {
      alert('请先登录后再分享');
      navigate('/login');
      return;
    }
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_BASE_URL}/stories/${storyId}/share`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token || ''}`,
        },
      });
      const data = await res.json();
      if (data.success) {
        setShareData({ token: data.data.token, url: data.data.shareUrl });
        setShowShareModal(true);
      } else {
        alert(data.error || '创建分享链接失败');
      }
    } catch (err) {
      alert('创建分享链接失败');
    }
  }, [storyId, isAuthenticated, navigate]);

  // 发表评论
  const handleSubmitComment = async () => {
    if (!storyId || !commentText.trim()) return;
    setSubmittingComment(true);
    try {
      const res = await fetch(`${API_BASE_URL}/stories/${storyId}/reader/comments`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          content: commentText.trim(),
          nickname: commentNickname.trim() || null,
        }),
      });
      const data = await res.json();
      if (data.success) {
        setComments(prev => [data.data, ...prev]);
        setCommentCount(prev => prev + 1);
        setCommentText('');
      } else {
        alert(data.error || '评论失败');
      }
    } catch (err) {
      alert('评论失败，请稍后重试');
    } finally {
      setSubmittingComment(false);
    }
  };

  // 下载所有插图（通过后端接口打包为zip）
  const downloadImages = useCallback(async () => {
    if (!storyId) return;
    if (!isAuthenticated) {
      alert('请先登录后再下载');
      navigate('/login');
      return;
    }
    setIsDownloadingImages(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE_URL}/stories/${storyId}/images-zip`, {
        method: 'POST',
        headers: token ? { Authorization: `Bearer ${token}` } : undefined,
      });
      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.error || '打包下载失败');
      }
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${story?.title || 'story'}-images.zip`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err: any) {
      console.error('打包下载插图失败:', err);
      alert(err?.message || '打包下载插图失败，请稍后重试');
    } finally {
      setIsDownloadingImages(false);
    }
  }, [storyId, isAuthenticated, story?.title, navigate]);

  // 监听全屏变化
  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };
    
    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => document.removeEventListener('fullscreenchange', handleFullscreenChange);
  }, []);

  if (isLoading) {
    return (
      <div className="min-h-dvh flex items-center justify-center bg-gray-900">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-dvh flex flex-col items-center justify-center bg-gray-900 text-white">
        <p className="text-xl mb-4">{error}</p>
        <button 
          onClick={() => navigate(-1)}
          className="px-4 py-2 bg-blue-500 rounded-lg hover:bg-blue-600"
        >
          返回
        </button>
      </div>
    );
  }

  const currentPage = pages[currentIndex];

  return (
    <div 
      ref={containerRef}
      className="relative w-full h-dvh overflow-hidden"
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
    >
      {/* 隐藏音频元素 */}
      <audio ref={audioRef} />

      {mode === 'auto' ? (
        // ==================== 自动播放模式（听读）====================
        <div className="h-dvh flex flex-col bg-gray-900 overflow-hidden">
          {/* 顶部导航栏 */}
          <div className="absolute top-0 left-0 right-0 z-20 px-3 py-2 sm:px-4 sm:py-3 flex items-center justify-between bg-gray-900/60 backdrop-blur-sm text-white">
            <div className="flex items-center gap-2 sm:gap-3">
              <button onClick={() => navigate(-1)} className="p-1.5 sm:p-2 rounded-full hover:bg-white/10">
                <X className="w-5 h-5" />
              </button>
              <h1 className="font-medium truncate max-w-[120px] sm:max-w-md text-sm">
                {story?.title}
              </h1>
            </div>

            <div className="flex items-center gap-1 sm:gap-2">
              {/* 模式切换 */}
              <div className="flex items-center rounded-full p-1 bg-gray-800">
                <button
                  onClick={() => handleModeChange('auto')}
                  className="flex items-center gap-1 px-3 py-1.5 rounded-full text-sm font-medium bg-blue-500 text-white shadow-md"
                >
                  <Headphones className="w-4 h-4" />
                  <span className="hidden sm:inline">听读</span>
                </button>
                <button
                  onClick={() => handleModeChange('read-along')}
                  className="flex items-center gap-1 px-3 py-1.5 rounded-full text-sm font-medium text-gray-400 hover:text-white"
                >
                  <Eye className="w-4 h-4" />
                  <span className="hidden sm:inline">跟读</span>
                </button>
              </div>
              
              <button onClick={toggleFullscreen} className="p-2 rounded-full hover:bg-white/10 hidden sm:block">
                {isFullscreen ? <Minimize className="w-5 h-5" /> : <Maximize className="w-5 h-5" />}
              </button>

              {/* 分享按钮 - 所有登录用户可见 */}
              <button
                onClick={handleShare}
                className="flex items-center gap-1 px-2 py-1 rounded-full text-sm font-medium text-white hover:bg-white/10"
                title="分享绘本"
              >
                <Share2 className="w-4 h-4" />
                <span className="hidden sm:inline">分享</span>
              </button>

              {isOwner && (
                <>
                  <button
                    onClick={async () => {
                      if (!storyId) return;
                      if (!isAuthenticated) {
                        alert('请先登录后再下载');
                        navigate('/login');
                        return;
                      }
                      setIsDownloadingPDF(true);
                      try {
                        const token = localStorage.getItem('token');
                        const headers = token ? { Authorization: `Bearer ${token}` } : undefined;

                        // 先检查 PDF 是否已存在
                        const checkRes = await api.get(`/stories/${storyId}/pdf`, { headers });
                        let pdfUrl = checkRes.data?.data?.pdfUrl;

                        // 如果不存在，再生成
                        if (!pdfUrl) {
                          const genRes = await api.post(
                            `/stories/${storyId}/pdf`,
                            {},
                            {
                              headers,
                              timeout: 120000, // PDF生成耗时较长，单独设置2分钟超时
                            }
                          );
                          if (!genRes.data?.success) {
                            alert('PDF生成失败: ' + (genRes.data?.error || '未知错误'));
                            return;
                          }
                          pdfUrl = genRes.data?.data?.pdfUrl;
                        }

                        if (!pdfUrl) {
                          alert('PDF下载链接获取失败');
                          return;
                        }

                        // 通过后端代理下载，确保文件名正确（COS直链跨域时a.download不生效）
                        const downloadRes = await fetch(
                          `${API_BASE_URL}/stories/${storyId}/pdf?download=true`,
                          { headers: headers as any }
                        );
                        if (!downloadRes.ok) {
                          throw new Error('PDF下载请求失败');
                        }
                        const blob = await downloadRes.blob();
                        const url = window.URL.createObjectURL(blob);
                        const a = document.createElement('a');
                        a.href = url;
                        a.download = `${story?.title || 'story'}.pdf`;
                        a.click();
                        window.URL.revokeObjectURL(url);
                      } catch (err: any) {
                        console.error('PDF下载失败:', err);
                        alert(err?.response?.data?.error || 'PDF下载失败');
                      } finally {
                        setIsDownloadingPDF(false);
                      }
                    }}
                    disabled={isDownloadingPDF}
                    className="flex items-center gap-1 px-2 py-1 rounded-full text-sm font-medium text-white hover:bg-white/10 disabled:opacity-50"
                  >
                    {isDownloadingPDF ? (
                      <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    ) : (
                      <Download className="w-4 h-4" />
                    )}
                    <span className="hidden sm:inline">PDF</span>
                  </button>

                  <button
                    onClick={downloadImages}
                    disabled={isDownloadingImages}
                    className="flex items-center gap-1 px-2 py-1 rounded-full text-sm font-medium text-white hover:bg-white/10 disabled:opacity-50"
                  >
                    {isDownloadingImages ? (
                      <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    ) : (
                      <Images className="w-4 h-4" />
                    )}
                    <span className="hidden sm:inline">图片</span>
                  </button>

                  <button
                    onClick={async () => {
                      if (!storyId) return;
                      if (!isAuthenticated) {
                        alert('请先登录后再下载');
                        navigate('/login');
                        return;
                      }
                      setIsDownloadingHTML5(true);
                      try {
                        const token = localStorage.getItem('token');
                        const genRes = await api.post(
                          `/stories/${storyId}/html5`,
                          {},
                          token ? { headers: { Authorization: `Bearer ${token}` } } : undefined
                        );
                        if (!genRes.data?.success) {
                          alert('HTML5 包生成失败: ' + (genRes.data?.error || '未知错误'));
                          return;
                        }
                        const downloadUrl = `/api/stories/${storyId}/html5?download=true${token ? `&token=${token}` : ''}`;
                        const a = document.createElement('a');
                        a.href = downloadUrl;
                        a.download = `${story?.title || 'story'}.zip`;
                        a.click();
                      } catch (err: any) {
                        console.error('HTML5 下载失败:', err);
                        alert(err?.response?.data?.error || 'HTML5 下载失败');
                      } finally {
                        setIsDownloadingHTML5(false);
                      }
                    }}
                    disabled={isDownloadingHTML5}
                    className="flex items-center gap-1 px-2 py-1 rounded-full text-sm font-medium text-white hover:bg-white/10 disabled:opacity-50"
                  >
                    {isDownloadingHTML5 ? (
                      <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    ) : (
                      <Download className="w-4 h-4" />
                    )}
                    <span className="hidden sm:inline">离线包</span>
                  </button>
                </>
              )}
            </div>
          </div>

          {/* 图片区域：固定4:3比例，object-contain完整显示，全屏时最大化 */}
          <div className={`relative flex items-center justify-center bg-gradient-to-b from-gray-800 to-gray-900 transition-all duration-300 pt-11 sm:pt-14 px-4 md:px-8 ${isFullscreen ? 'h-[78%] md:h-[82%]' : 'h-[58%] sm:h-[60%] md:h-[70%]'}`}>
            <AnimatePresence mode="wait">
              <motion.div
                key={currentIndex}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.3 }}
                className="h-full max-w-full flex items-center justify-center py-2 md:py-4"
              >
                <img
                  src={currentPage?.imageUrl || ''}
                  alt={`Page ${currentPage?.pageNumber}`}
                  className="max-h-full max-w-full object-contain rounded-lg md:rounded-xl shadow-2xl"
                  style={{ aspectRatio: '4/3' }}
                />
              </motion.div>
            </AnimatePresence>
          </div>

          {/* 文字区域：全屏时缩小 */}
          <div className={`flex items-center justify-center px-4 md:px-6 relative z-10 transition-all duration-300 ${isFullscreen ? 'h-[17%] md:h-[13%]' : 'h-[20%] sm:h-[25%] md:h-[20%]'}`}>
            <AnimatePresence mode="wait">
              <motion.div
                key={currentIndex}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.3 }}
                className="bg-white/95 backdrop-blur-md rounded-2xl shadow-2xl p-4 md:p-6 max-w-3xl w-full border border-white/20"
              >
                <p className="text-base md:text-lg lg:text-xl text-gray-800 text-center leading-relaxed line-clamp-4 font-medium">
                  {currentPage?.content}
                </p>
              </motion.div>
            </AnimatePresence>
          </div>

          {/* 音频控制：全屏时压缩 */}
          <div className={`bg-gray-900 flex items-center justify-center px-4 md:px-6 gap-3 md:gap-6 text-white transition-all duration-300 ${isFullscreen ? 'h-[5%] md:h-[5%]' : 'h-[10%] sm:h-[15%] md:h-[10%]'}`}>
            <button 
              onClick={goPrev}
              disabled={currentIndex === 0}
              className="text-xl opacity-80 hover:opacity-100 disabled:opacity-30 transition"
            >
              <ChevronLeft className="w-6 h-6" />
            </button>
            
            <button 
              onClick={togglePlay}
              disabled={!currentPage?.audioUrl}
              className="w-12 h-12 md:w-14 md:h-14 rounded-full bg-blue-500 flex items-center justify-center text-xl shadow-lg hover:scale-105 transition disabled:opacity-50"
            >
              {isPlaying ? <Pause className="w-5 h-5" /> : <Play className="w-5 h-5 ml-0.5" />}
            </button>
            
            <button 
              onClick={goNext}
              disabled={currentIndex === pages.length - 1}
              className="text-xl opacity-80 hover:opacity-100 disabled:opacity-30 transition"
            >
              <ChevronRight className="w-6 h-6" />
            </button>

            {/* 音频进度条 */}
            {currentPage?.audioUrl && (
              <div className="hidden sm:flex items-center gap-2 flex-1 max-w-[200px]">
                <div className="flex-1 h-2 bg-gray-700 rounded-full overflow-hidden">
                  <div 
                    className="h-full bg-blue-500 rounded-full transition-all"
                    style={{ width: `${audioDuration ? (audioProgress / audioDuration) * 100 : 0}%` }}
                  />
                </div>
                <span className="text-xs font-mono text-gray-400">
                  {Math.floor(audioProgress / 60)}:{String(Math.floor(audioProgress % 60)).padStart(2, '0')}
                </span>
              </div>
            )}

            {/* 页码 */}
            <span className="text-sm font-mono text-gray-400">
              {currentIndex + 1}/{pages.length}
            </span>

            {/* 统计按钮组 */}
            <div className="flex items-center gap-3 sm:gap-4">
              {/* 浏览量 */}
              <span className="flex items-center gap-1 text-gray-400 text-sm" title="阅读量">
                <Eye className="w-4 h-4" />
                {viewCount}
              </span>

              {/* 播放量 */}
              <span className="flex items-center gap-1 text-gray-400 text-sm" title="播放量">
                <Play className="w-4 h-4" />
                {playCount}
              </span>

              {/* 评论 */}
              <button
                onClick={() => setShowCommentModal(true)}
                className="flex items-center gap-1 text-gray-400 hover:text-white transition text-sm"
                title="评论"
              >
                <MessageCircle className="w-4 h-4" />
                {commentCount}
              </button>

              {/* 点赞 */}
              <button
                onClick={async () => {
                  if (!storyId || !isAuthenticated) {
                    alert('请先登录后再点赞');
                    navigate('/login');
                    return;
                  }
                  if (isLiking) return;
                  setIsLiking(true);
                  try {
                    const res = await fetch(`${API_BASE_URL}/stories/${storyId}/reader/like`, {
                      method: 'POST',
                      headers: {
                        'Content-Type': 'application/json',
                        Authorization: `Bearer ${localStorage.getItem('token') || ''}`,
                      },
                    });
                    const data = await res.json();
                    if (data.success) {
                      setLikeCount(data.data.likeCount);
                    } else {
                      alert(data.error || '点赞失败');
                    }
                  } catch (err) {
                    alert('点赞失败，请稍后重试');
                  } finally {
                    setIsLiking(false);
                  }
                }}
                disabled={isLiking}
                className="flex items-center gap-1 text-rose-400 hover:text-rose-300 disabled:opacity-50 transition"
                title="点赞"
              >
                <Heart className={`w-5 h-5 ${isLiking ? '' : 'fill-current'}`} />
                <span className="text-sm font-medium">{likeCount}</span>
              </button>
            </div>
          </div>
        </div>
      ) : (
        // ==================== 跟读模式（逐句高亮）====================
        <ReadAlongMode
          page={{
            imageUrl: currentPage?.imageUrl || '',
            content: currentPage?.content || '',
            pageNumber: currentPage?.pageNumber
          }}
          currentIndex={currentIndex}
          totalPages={pages.length}
          storyId={storyId}
          onNextPage={goNext}
          onPrevPage={goPrev}
          onExit={() => navigate(-1)}
          onModeChange={(newMode) => {
            handleModeChange(newMode);
          }}
        />
      )}

      {/* 分享弹窗 */}
      {showShareModal && shareData && (
        <SharePoster
          storyTitle={story?.title && story.title !== 'Creating your story...' ? story.title : '未命名绘本'}
          storyCover={story?.coverUrl || pages[0]?.imageUrl || null}
          sharerNickname={user?.nickname || '匿名用户'}
          sharerAvatar={user?.avatar || null}
          shareUrl={shareData.url}
          onClose={() => setShowShareModal(false)}
        />
      )}

      {/* 评论弹窗 */}
      {showCommentModal && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/60 p-0 sm:p-4" onClick={() => setShowCommentModal(false)}>
          <motion.div
            initial={{ opacity: 0, y: 100 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 100 }}
            onClick={(e) => e.stopPropagation()}
            className="bg-white rounded-t-2xl sm:rounded-2xl shadow-xl w-full max-w-md max-h-[80vh] flex flex-col"
          >
            <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100">
              <h3 className="text-lg font-bold text-slate-800">评论 ({commentCount})</h3>
              <button onClick={() => setShowCommentModal(false)} className="p-1 rounded-full hover:bg-slate-100">
                <X className="w-5 h-5 text-slate-500" />
              </button>
            </div>

            {/* 评论列表 */}
            <div className="flex-1 overflow-y-auto p-5 space-y-4">
              {comments.length === 0 ? (
                <p className="text-center text-slate-400 py-8">暂无评论，来说两句吧~</p>
              ) : (
                comments.map((comment) => (
                  <div key={comment.id} className="flex gap-3">
                    <div className="w-9 h-9 rounded-full bg-indigo-100 flex items-center justify-center flex-shrink-0 overflow-hidden">
                      {comment.avatar ? (
                        <img src={comment.avatar} alt="" className="w-full h-full object-cover" />
                      ) : (
                        <User className="w-4 h-4 text-indigo-500" />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium text-slate-700">{comment.nickname || '匿名用户'}</span>
                        <span className="text-xs text-slate-400">
                          {new Date(comment.createdAt).toLocaleString('zh-CN', {
                            month: 'short',
                            day: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit',
                          })}
                        </span>
                      </div>
                      <p className="text-sm text-slate-600 mt-0.5 break-words">{comment.content}</p>
                    </div>
                  </div>
                ))
              )}
            </div>

            {/* 评论输入 */}
            <div className="border-t border-slate-100 p-4 space-y-2">
              <input
                type="text"
                placeholder="你的昵称（可选）"
                value={commentNickname}
                onChange={(e) => setCommentNickname(e.target.value)}
                className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-blue-300"
                maxLength={30}
              />
              <div className="flex gap-2">
                <input
                  type="text"
                  placeholder="写下你的评论..."
                  value={commentText}
                  onChange={(e) => setCommentText(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && !submittingComment && commentText.trim() && handleSubmitComment()}
                  className="flex-1 px-3 py-2 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-blue-300"
                  maxLength={500}
                />
                <button
                  onClick={handleSubmitComment}
                  disabled={!commentText.trim() || submittingComment}
                  className="px-3 py-2 rounded-lg bg-blue-500 text-white hover:bg-blue-600 disabled:opacity-40 disabled:cursor-not-allowed transition"
                >
                  {submittingComment ? (
                    <span className="text-sm">发送中</span>
                  ) : (
                    <Send className="w-4 h-4" />
                  )}
                </button>
              </div>
            </div>
          </motion.div>
        </div>
      )}

      {/* 左右翻页按钮 (PC端显示) —— 仅在自动播放模式下显示 */}
      {mode === 'auto' && (
        <>
          <button
            onClick={goPrev}
            disabled={currentIndex === 0}
            className="absolute left-2 md:left-4 top-1/2 -translate-y-1/2 p-2 md:p-3 rounded-full bg-black/20 text-white hover:bg-black/40 disabled:opacity-0 hidden sm:block transition-all z-30"
          >
            <ChevronLeft className="w-5 h-5 md:w-6 md:h-6" />
          </button>
          
          <button
            onClick={goNext}
            disabled={currentIndex === pages.length - 1}
            className="absolute right-2 md:right-4 top-1/2 -translate-y-1/2 p-2 md:p-3 rounded-full bg-black/20 text-white hover:bg-black/40 disabled:opacity-0 hidden sm:block transition-all z-30"
          >
            <ChevronRight className="w-5 h-5 md:w-6 md:h-6" />
          </button>

          {/* 点击区域翻页 (移动端) */}
          <div className="absolute left-0 top-0 w-1/4 h-full sm:hidden z-10" onClick={goPrev} />
          <div className="absolute right-0 top-0 w-1/4 h-full sm:hidden z-10" onClick={goNext} />
        </>
      )}
    </div>
  );
}
